import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GlobalNotificationBar({ user }) {
  const [notifications, setNotifications] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [textVisible, setTextVisible] = useState(true);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/global-notifications`);
      const active = (res.data || []).filter(n => n.is_active);
      setNotifications(active);
      setCurrentIndex(0);
      setTextVisible(true);
    } catch {
      // Silent fail — notification bar is non-critical
    }
  }, []);

  useEffect(() => {
    if (user) fetchNotifications();
  }, [user, fetchNotifications]);

  // Rotation: fade text out, advance index, fade text back in
  useEffect(() => {
    if (notifications.length <= 1) return;

    const current = notifications[currentIndex];
    const displayMs = ((current?.display_time) || 10) * 1000;

    const rotateTimer = setTimeout(() => {
      setTextVisible(false);
      setTimeout(() => {
        setCurrentIndex(prev => (prev + 1) % notifications.length);
        setTextVisible(true);
      }, 700); // matches fade-out duration
    }, displayMs);

    return () => clearTimeout(rotateTimer);
  }, [currentIndex, notifications]);

  if (!notifications.length) return null;

  const current = notifications[currentIndex] || notifications[0];

  const fadeStyle = {
    opacity: textVisible ? 1 : 0,
    transition: 'opacity 0.65s ease',
  };

  return (
    // Scrolls with page - balanced spacing above and below
    <div style={{ padding: '12px 24px 4px', display: 'flex', justifyContent: 'center', backgroundColor: 'transparent' }}>
      {/* Gradient border wrapper — predominantly yellow, soft orange only at edges */}
      <div
        data-testid="global-notification-bar"
        style={{
          background: 'linear-gradient(90deg, #FCD34D 0%, #F59E0B 40%, #F59E0B 60%, #F97316 100%)',
          borderRadius: '14px',
          padding: '2px',
          boxShadow: '0 4px 14px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06)',
          width: '100%',
          maxWidth: '1400px',
        }}
      >
        {/* Inner white pill */}
        <div
          style={{
            backgroundColor: '#ffffff',
            borderRadius: '12px',
            padding: '7px 18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            minHeight: '34px',
            whiteSpace: 'nowrap',
          }}
        >
          {/* Icon — always visible, does not fade */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#F59E0B"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            style={{ flexShrink: 0 }}
          >
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>

          {/* Text — fades when rotating */}
          <span
            style={{
              fontSize: '13px',
              fontWeight: 600,
              color: '#78350F',
              letterSpacing: '0.01em',
              ...fadeStyle,
            }}
          >
            {current.text}
          </span>

          {current.link_url && (
            <a
              href={current.link_url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="global-notification-link"
              style={{
                fontSize: '12px',
                fontWeight: 700,
                color: '#92400E',
                textDecoration: 'underline',
                textUnderlineOffset: '2px',
                marginLeft: '4px',
                whiteSpace: 'nowrap',
                ...fadeStyle,
              }}
            >
              {current.link_name || 'View'}
            </a>
          )}

          {notifications.length > 1 && (
            <span
              style={{
                fontSize: '11px',
                color: '#B45309',
                marginLeft: '8px',
                flexShrink: 0,
                opacity: textVisible ? 0.7 : 0,
                transition: 'opacity 0.65s ease',
              }}
            >
              {currentIndex + 1}/{notifications.length}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
